import { useState } from 'react';
import { collectError } from 'utils';

import { getDataFromForm } from '../../shared/form';
import { isFormValid, isPasswordCorrect, validateNewPasswordForm } from './validation';
import type { NewPasswordFormData } from '../../types/NewPassword';
import type { NewPasswordFormErrors } from '../../components/NewPassword';
import NewPassword from '../../components/NewPassword';
import { newPasswordFieldNames } from '../../shared/constants';
import router, { FormRoutes } from '../../../../common/utils/routing';

const State = {
    submitting: 'submitting',
    editing: 'editing',
    complete: 'complete',
} as const;
type State = (typeof State)[keyof typeof State];

interface NewPasswordContainerProps {
    message?: React.ReactNode;
    changePassword: (newPassword: string) => Promise<{ passwordVerificationRequired?: boolean }>;
}

const NewPasswordContainer: React.FC<NewPasswordContainerProps> = ({ message, changePassword }) => {
    const [state, setState] = useState<State>(State.editing);
    const [formApiError, setFormApiError] = useState<string | null>(null);
    const [errors, setErrors] = useState<NewPasswordFormErrors>(
        () =>
            validateNewPasswordForm({
                [newPasswordFieldNames.newPassword]: '',
                [newPasswordFieldNames.confirmPassword]: '',
            }).errors,
    );

    const validateOnChange = (event: React.FormEvent<HTMLFormElement>) => {
        setFormApiError(null);
        const { errors } = validateNewPasswordForm(getDataFromForm<NewPasswordFormData>(event));
        setErrors(errors);
    };

    async function onSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
        event.preventDefault();

        const data = getDataFromForm<NewPasswordFormData>(event);
        const { errors, isValid } = validateNewPasswordForm(data);

        if (!isValid) {
            return setErrors(errors);
        }

        try {
            setState(State.submitting);
            const { passwordVerificationRequired } = await changePassword(data[newPasswordFieldNames.newPassword]);

            setState(State.complete);

            if (passwordVerificationRequired) {
                return router.go(FormRoutes.checkInbox);
            }

            router.go(FormRoutes.login, '?message=password.changed');
        } catch (error: unknown) {
            const errorMessage = error instanceof Error ? error.message : null;
            setFormApiError(errorMessage);
            setState(State.editing);
            collectError(error);
        }
    }

    const formError =
        formApiError ||
        (isPasswordCorrect(errors[newPasswordFieldNames.newPassword]) &&
            errors[newPasswordFieldNames.confirmPassword]) ||
        null;
    const isValid = isFormValid(errors);

    return (
        <NewPassword
            errors={errors}
            formError={formError}
            isSubmitting={state === State.submitting}
            message={message}
            onChange={validateOnChange}
            onSubmit={onSubmit}
            isError={!isValid || !!formApiError}
            isSubmitDisable={!isValid}
        />
    );
};

export default NewPasswordContainer;
